import { motion } from "framer-motion";
import { Check, ShieldCheck, FileCheck } from "lucide-react";
import { INSURANCE_OPTIONS, ADDONS } from "@/lib/data";
import { SectionHead } from "./SectionHead";
import { useBooking } from "@/context/BookingContext";
import { usePublicPricing } from "@/lib/usePublicPricing";

export const Pricing = () => {
  const { openBooking } = useBooking();
  const pricing = usePublicPricing();
  const Icons = { BYOI: FileCheck, AllIn: ShieldCheck };

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-24 md:py-32 border-t border-white/5 bg-[#080808]">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <SectionHead center overline="Simple Company-Wide Pricing" title="Two Offers." accent="Zero Guesswork." desc="Every vehicle, one simple price. Coverage and optional protection are always calculated separately." />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14 max-w-5xl mx-auto" data-testid="package-pricing-cards">
          <div className="rounded-xl p-8 border bg-[#0C0C0C] border-white/10">
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">3-Day Rental Special</div>
            <div className="font-display font-black text-5xl text-gold-gradient mt-3">${pricing.three_day_package}</div>
            <div className="text-white/40 text-sm mt-1">for 3 full days · any vehicle</div>
            <p className="text-white/55 text-sm mt-4 font-light">Perfect for weekend trips, temporary transportation, business travel, or when you need a dependable vehicle for a few days.</p>
            <button data-testid="package-book-3day" onClick={() => openBooking("")}
              className="mt-6 w-full py-3 rounded-md font-bold border border-white/20 hover:bg-white hover:text-black transition-colors duration-300">
              Book the 3-Day Special
            </button>
          </div>
          <div className="relative rounded-xl p-8 border bg-[#0C0C0C] border-[#F2B705] shadow-[0_0_40px_-10px_rgba(242,183,5,0.35)]">
            <span className="absolute -top-3 left-8 bg-[#F2B705] text-black text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">Best Value</span>
            <div className="text-xs uppercase tracking-[0.25em] text-[#F2B705]/90">Weekly Special</div>
            <div className="font-display font-black text-5xl text-white mt-3">${pricing.weekly_special}</div>
            <div className="text-white/40 text-sm mt-1">for 7 full days · save ${pricing.weekly_savings}</div>
            <p className="text-white/55 text-sm mt-4 font-light">Need the vehicle longer? Save with our seven-day Weekly Special — the smartest way to stay moving all week.</p>
            <button data-testid="package-book-weekly" onClick={() => openBooking("")}
              className="mt-6 w-full py-3 rounded-md font-bold bg-[#F2B705] text-black hover:bg-white transition-colors duration-300">
              Book the Weekly Special
            </button>
          </div>
          <div className="rounded-xl p-8 border bg-[#0C0C0C] border-white/10">
            <div className="text-xs uppercase tracking-[0.25em] text-white/45">Additional Days</div>
            <div className="font-display font-black text-5xl text-white/85 mt-3">${pricing.additional_day_rate}<span className="text-lg text-white/40 font-normal">/day</span></div>
            <div className="text-white/40 text-sm mt-1">after your package</div>
            <p className="text-white/55 text-sm mt-4 font-light">Keep the keys as long as you need. Every day past your 3-day or weekly package is one flat rate — no surprises.</p>
            <p className="text-white/35 text-xs mt-4">All-In Coverage +${pricing.allin_coverage_day}/day · Damage Waiver +${pricing.damage_waiver_day}/day — always itemized separately.</p>
          </div>
        </div>

        <p data-testid="calendar-day-policy" className="text-white/40 text-sm text-center mt-8 max-w-2xl mx-auto font-light">
          Rental days are counted by <span className="text-white/65">calendar date from pickup to return</span>, at your agreed pickup/return times. Returning after the scheduled return time may result in additional late-return charges. Minimum rental: 3 calendar days.
        </p>

        <div className="mt-24">
          <SectionHead center overline="How It Works" title="Choose Your" accent="Coverage." desc="Two simple ways to drive. Bring your own insurance for the lowest rates, or let us cover you with All-In." />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14 max-w-4xl mx-auto">
          {INSURANCE_OPTIONS.map((opt, i) => {
            const Icon = Icons[opt.id];
            return (
              <motion.div
                key={opt.id}
                data-testid={`insurance-option-${opt.id.toLowerCase()}`}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`relative rounded-xl p-8 border ${
                  opt.featured
                    ? "bg-[#0C0C0C] border-[#F2B705] shadow-[0_0_40px_-10px_rgba(242,183,5,0.35)]"
                    : "bg-[#0C0C0C] border-white/10"
                }`}
              >
                {opt.featured && (
                  <span className="absolute -top-3 left-8 bg-[#F2B705] text-black text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
                    No Insurance? Start Here
                  </span>
                )}
                <div className="w-12 h-12 rounded-lg bg-[#F2B705]/10 border border-[#F2B705]/20 flex items-center justify-center">
                  <Icon className="text-[#F2B705]" size={22} />
                </div>
                <h3 className="font-display font-bold text-2xl mt-5">{opt.name}</h3>
                <p className="text-white/50 text-sm mt-2 font-light">{opt.desc}</p>
                <ul className="mt-6 space-y-3">
                  {opt.perks.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-sm text-white/70">
                      <Check size={17} className="text-[#F2B705] shrink-0 mt-0.5" />
                      {perk}
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`insurance-book-${opt.id.toLowerCase()}`}
                  onClick={() => openBooking("")}
                  className={`mt-8 w-full py-3 rounded-md font-bold transition-colors duration-300 ${
                    opt.featured ? "bg-[#F2B705] text-black hover:bg-white" : "border border-white/20 hover:bg-white hover:text-black"
                  }`}
                >
                  Request With {opt.short}
                </button>
              </motion.div>
            );
          })}
        </div>

        {/* Add-ons + fees */}
        <div className="mt-10 max-w-4xl mx-auto grid grid-cols-1 sm:grid-cols-3 gap-4">
          {[ADDONS.extraDriver, ADDONS.damageProtection, ADDONS.youngDriverFee].map((a) => (
            <div key={a.label} className="bg-[#0C0C0C] border border-white/10 rounded-lg p-5 text-center">
              <div className="text-gold-gradient font-display font-black text-2xl">${a.price}<span className="text-xs text-white/40 font-normal">{a.unit}</span></div>
              <div className="text-white/55 text-sm mt-1">{a.label}</div>
            </div>
          ))}
        </div>
        <p className="text-center text-white/35 text-sm mt-6">No standard security deposit required. Additional verification may apply based on risk factors.</p>
      </div>
    </section>
  );
};
